import { useState } from 'react';
import { Auth } from "../../../../Common/lib/auth";

export default function AccountInfoSection({ userProfile, getUserSession, setUserProfile }) {
  const [userUpdate, setUserUpdate] = useState(false);
  const [profileEdit, setProfileEdit] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});
  const [profileError, setProfileError] = useState("");
  const emailRegex = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,6}$/;
  const phoneRegex = /^\+1\s?([2-9]{1}[0-9]{2})[-\s]?([0-9]{3})[-\s]?([0-9]{4})$/;



  /**
   * Handles profile input field changes.
   * @param {*} field Name of userPassword property to change
   * @param {*} value Value from input field
   */
  function handleProfileChange(field, value) {
    setUserProfile((prevState) => ({
      ...prevState,
      [field]: value.trim(),
    }));
  }

  /**
   * Cancels profile editing mode and clears error messages.
   */
  const cancelUpdateProfile = async () => {
    getUserSession();
    setProfileEdit(false);
    setProfileError("");
  };

  /**
   * Saves User Information.
   * 
   * @param {Event Handler} e 
   */
  const updateProfile = async (e) => {
    e.preventDefault();
    setProfileError("");

    let hasErrors = false;
    let newFieldErrors = {};

    // Validate fields
    for (const [key, value] of Object.entries(userProfile)) {
      if (value === "") {
        newFieldErrors[key] = "This field is required.";
        hasErrors = true;
      } else if (key === 'email' && !emailRegex.test(value)) {
        newFieldErrors[key] = "Invalid email format.";
        hasErrors = true;
      } else if (key === 'phone_number' && !phoneRegex.test(value)) {
        newFieldErrors[key] = "Please input +1 with North American phone number.";
        hasErrors = true;
      } else {
        newFieldErrors[key] = "";
      }
    }

    setFieldErrors(newFieldErrors);
    if (hasErrors) return;

    const user = await Auth.currentAuthenticatedUser();
    // Update user info
    await Auth.updateUserAttributes(user, {
      phone_number: userProfile.phone_number,
      institute: userProfile.institute,
      research_geo: userProfile.research_geo,
      research_field: userProfile.research_field,
      email: userProfile.email,
      given_name: userProfile.fname,
      family_name: userProfile.lname,
    })
      .then(() => {
        setUserUpdate(!userUpdate);
        setProfileEdit(false);
      })
      .catch((err) => {
        setProfileError("There was an error updating user information");
      });
  };

  return (
    <div className="bg-gray-50 border border-gray-200 shadow-sm rounded-xl p-8 dark:bg-gray-800 dark:border-gray-700">
      <div className="flex justify-between items-center">
        <h1 className="text-base font-semibold leading-7 text-gray-900 dark:text-white">Profile</h1>
        {profileEdit ? (
          <div className="flex">
            <button
              onClick={(e) => updateProfile(e)}
              type="button"
              className="rounded-md w-full bg-blue-600 py-1 px-4 text-sm font-medium text-white shadow-sm hover:bg-blue-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              data-cy="save-info"
            >
              Save
            </button>
            <button
              onClick={cancelUpdateProfile}
              type="button"
              className="ml-2 rounded-md w-full py-1 px-4 text-sm font-medium shadow-sm text-gray-700 bg-gray-300 hover:bg-gray-400 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 dark:border dark:bg-gray-700 dark:text-gray-200 dark:border-gray-600 dark:hover:text-white dark:hover:bg-gray-600"
            >
              Cancel
            </button>
          </div>
        ) : (
          <button
              onClick={() => setProfileEdit(true)}
              type="button" className="font-semibold text-sm text-blue-600 hover:text-blue-500 dark:text-blue-500"
              data-cy="edit-info"
            >
            Edit
          </button>
        )}
      </div>

      <dl className="mt-2 space-y-6 divide-y divide-gray-100 border-t border-gray-200 text-sm leading-6 dark:divide-gray-700 dark:border-gray-700">

      {[
    { label: "First Name", field: "fname", cy: "first-name", maxLength: 20 },
    { label: "Last Name", field: "lname", cy: "last-name", maxLength: 50 },
    { label: "Email", field: "email", cy: "email" },
    { label: "Phone Number", field: "phone_number", cy: "phone-number" },
    { label: "Field of Research", field: "research_field", cy: "research-field" },
    { label: "University/Research Institute", field: "institute", cy: "research-institute" },
    { label: "Location of Research", field: "research_geo", cy: "research-location" }
].map(({ label, field, cy, maxLength }) => (
    <div className="pt-6 sm:flex items-center" key={field}>
        <dt className="font-medium text-gray-900 sm:w-64 sm:flex-none sm:pr-6 dark:text-white">{label}</dt>
        <dd className="mt-1 flex flex-col justify-between gap-x-6 sm:mt-0 sm:flex-auto">
            {profileEdit ? (
                <>
                    <input
                        onChange={(e) => handleProfileChange(field, e.target.value)}
                        defaultValue={userProfile[field]}
                        type="text"
                        data-cy={cy}
                        maxLength={maxLength}  // Add this line
                        className={`max-w-md border outline-none focus:ring-1 ${fieldErrors[field] ? "border-red-500" : "border-gray-300 dark:border-gray-600"} text-sm sm:text-base py-2 px-4 block w-full shadow-sm focus:ring-blue-500 focus:border-blue-500 rounded-md dark:bg-gray-700 dark:text-white`}
                    />
                    {fieldErrors[field] && <div className="text-red-600 text-xs mt-1">{fieldErrors[field]}</div>}
                </>
            ) : (
                <div className="text-gray-900 dark:text-white">{userProfile[field]}</div>
            )}
        </dd>
    </div>
))}


{profileError.length !== 0 &&
    <p className="italic text-sm block text-red-600">{profileError}</p>
}
</dl>
    </div>
  );
}